import { english, type Translate } from "../i18n/translate";
// ---------------------------------------------------------------------------
// WHOSE CLOCK A TYPED TIME IS READ IN.
//
// ★ THE DEFECT THIS EXISTS FOR, REPRODUCED BEFORE IT WAS WRITTEN.
//
// Creating an event through the console, `16:00` was typed into the `Starts`
// field and `15:00 Europe/London` was stored. Not a bug in the conversion — a
// `datetime-local` input carries NO zone, so `new Date(value)` reads it in the
// BROWSER's zone and `.toISOString()` converts from there. The browser was on
// CEST; the organisation runs on Europe/London; the hour moved.
//
// A scheduler in Budapest planning a London match day is given a kickoff time in
// London time, types it, and books the crew an hour early. Every shift window,
// pay figure and client-facing readiness number downstream inherits it.
//
// ★ WHAT THIS DOES AND DELIBERATELY DOES NOT DO.
//
// It does NOT convert. Interpreting the field in the organisation's zone instead
// of the browser's is a change to what the control MEANS, and it is a product
// decision rather than a bug fix: the same question is already open in
// `FINDING-no-shipped-client-can-set-an-organisations-clock`, where every
// organisation is hard-coded to Europe/London and no route can change it.
// Guessing an answer and shipping the guess would be worse than the defect,
// because a silent conversion is exactly what caused it.
//
// What it does is remove the SILENT half. The event detail screen already says
// "03:00 PM GMT+1 (04:00 PM your time)" — the disclosure convention exists and
// the INPUT simply lacked it. An hour that is wrong and labelled is a mistake
// somebody catches; an hour that is wrong and unlabelled is one that ships.
// ---------------------------------------------------------------------------

/** The viewer's own IANA zone, e.g. `Europe/Budapest`. Empty when unavailable. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
  } catch {
    // A browser that cannot answer is not a reason to render nothing readable.
    return "";
  }
}

/** The short zone name for a moment, e.g. `CEST`. Empty when unavailable. */
export function shortZoneName(at: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName");
    return part?.value ?? "";
  } catch {
    return "";
  }
}

/**
 * The sentence that goes under a `datetime-local` field.
 *
 * `organisationZone` is what the event's organisation runs on. It is optional
 * because the session's organisation block declares it optional — a caller that
 * has not got it still gets the half of the sentence that matters, which is that
 * the field is read in the reader's OWN time.
 *
 * ★ IT NAMES BOTH ZONES ONLY WHEN THEY DIFFER. On a London browser planning a
 * London event the second clause is noise, and a hint that is noise on the
 * common path is a hint people stop reading on the uncommon one.
 */
export function typedTimeHint(organisationZone?: string | null, t: Translate = english): string {
  const mine = browserTimeZone();
  const label = shortZoneName();
  const own = label !== "" ? t("your own time ({zone})", { zone: label }) : t("your own time");

  if (!organisationZone || organisationZone === "" || organisationZone === mine) {
    return t("Entered in {own}.", { own });
  }
  return t("Entered in {own} — this organisation runs on {zone}, so the stored time may differ from what you type.", {
    own,
    zone: organisationZone,
  });
}
